import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout/Layout';
import {
    Autocomplete,
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import SearchField from '../../components/input/SearchField';
import { fetchCustomerData } from '../../redux/slices/customerSlice';
import { fetchRepsData } from '../../redux/slices/repSlice';
import { fetchProductData } from '../../redux/slices/productSlice';
import { useDispatch, useSelector } from 'react-redux';
import { apiRequest } from '../../utils/api';
import moment from 'moment';
import ErrorScreen from '../../components/ErrorScreen';
import Loader from '../../components/Loader';
import ReactToPrint, { useReactToPrint } from 'react-to-print';
import OrderItemPrint from '../../print/OrderItemPrint';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { exportToExcel } from '../../utils/exportToExcel';

export default function DispatchedItems() {
    const dispatch = useDispatch();

    const { data: products, isLoading: isLoadingProducts, error: errorProducts } = useSelector((state) => state.product);
    const { data: customers, isLoading: isLoadingCustomers, error: errorCustomers } = useSelector((state) => state.customer);
    const { data: reps, isLoading: isLoadingReps, error: errorReps } = useSelector((state) => state.rep);

    const [selectedRep, setSelectedRep] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState([]);
    const [selectedStatus, setSelectedStatus] = React.useState([]);

    const curDate = new Date();
    const [startDate, setStartDate] = useState(
        new Date(curDate.getFullYear(), curDate.getMonth(), curDate.getDate() - 2).toLocaleDateString('en-CA')
    );
    const [endDate, setEndDate] = useState(new Date().toLocaleDateString('en-CA'));

    const [orderItems, setOrderItems] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const handleStartDate = (e) => {
        setStartDate(e?.target?.value);
    };

    const handleEndDate = (e) => {
        setEndDate(e?.target?.value);
    };

    useEffect(() => {
        if (!customers) {
            dispatch(fetchCustomerData());
        }
        if (!reps) {
            dispatch(fetchRepsData());
        }
        if (!products) {
            dispatch(fetchProductData());
        }

        return () => {
            setSelectedCustomer(null);
            setSelectedRep(null);
            setSelectedProduct(null);
        };
    }, []);

    const handleFilter = async () => {
        setIsLoading(true);
        setOrderItems(null);
        setError(null);

        let productIds = selectedProduct?.map((i) => i?.id);
        let repIds = selectedRep?.map((i) => i?.id);
        let customerIds = selectedCustomer?.map((i) => i?.id);

        let data = {
            productId: productIds?.length > 0 ? productIds : null,
            repId: repIds?.length > 0 ? repIds : null,
            customerId: customerIds?.length > 0 ? customerIds : null,
            status: selectedStatus?.length > 0 ? selectedStatus : ['Dispatched', 'Cancelled'],
            startDate: startDate,
            endDate: endDate,
            dateFilterField: 'dispatchDate'
        };

        try {
            const res = await apiRequest('post', `orderItems`, data);

            setOrderItems(res?.data);
        } catch (error) {
            setError(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        handleFilter();

        return () => {
            setOrderItems(null);
            setIsLoading(true);
            setError(null);
        };
    }, []);

    const componentRef = React.useRef(null);
    const handlePrint = useReactToPrint({
        content: () => componentRef.current
    });

    const [expandedRow, setExpandedRow] = useState(null);

    const handleRowClick = (index) => {
        setExpandedRow(expandedRow === index ? null : index);
    };

    const handleExport = async () => {
        try {
            const transformedData = orderItems.map((item) => ({
                Product_Name: item?.productName,
                Total_Qty_Order: item?.totalQtyOrder,
                Total_Qty_Dispatch: item?.totalQtyDispatch,
                Total_Qty_Remaining: item?.totalQtyRemaining
            }));

            if (!!orderItems) {
                exportToExcel(transformedData, `Order_Items_Disp_Cancel.xlsx`);
            }
        } catch (error) {
            return toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        }
    };

    return (
        <Layout>
            <Grid container sx={{ mb: 2 }} spacing={2} alignItems={'center'}>
                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={products ?? []}
                        defaultValue={[]}
                        getOptionLabel={(prod) => `${prod?.name}`}
                        // value={selectedProduct}
                        onChange={(event, selected) => {
                            setSelectedProduct(selected);
                        }}
                        renderInput={(params) => <TextField {...params} label="Product" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={reps ?? []}
                        defaultValue={[]}
                        getOptionLabel={(rep) => `${rep?.firstName} ${rep?.lastName} - ${rep?.phoneNo}`}
                        // value={selectedRep}
                        onChange={(event, selected) => {
                            setSelectedRep(selected);
                        }}
                        renderInput={(params) => <TextField {...params} label="Rep" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={customers ?? []}
                        defaultValue={[]}
                        getOptionLabel={(customer) => `${customer?.name} - ${customer?.phoneNo}`}
                        // value={selectedCustomer}
                        onChange={(event, selected) => {
                            setSelectedCustomer(selected);
                        }}
                        renderInput={(params) => <TextField {...params} label="Customer" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="demo-simple-select-label">Status</InputLabel>
                        <Select label="Status" multiple value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                            <MenuItem value={'Dispatched'}>Dispatched</MenuItem>
                            <MenuItem value={'Cancelled'}>Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item lg={1.25} xs={6}>
                    <TextField
                        size="small"
                        fullWidth
                        type="date"
                        variant="outlined"
                        label="Start Date"
                        InputLabelProps={{ shrink: true }}
                        value={startDate}
                        onChange={handleStartDate}
                    />
                </Grid>

                <Grid item lg={1.25} xs={6}>
                    <TextField
                        size="small"
                        fullWidth
                        type="date"
                        variant="outlined"
                        label="End Date"
                        InputLabelProps={{ shrink: true }}
                        value={endDate}
                        onChange={handleEndDate}
                    />
                </Grid>

                <Grid item lg={1} sm={3.5} md={2.5} xs={12}>
                    <Button onClick={handleFilter} fullWidth variant="contained" sx={{ textTransform: 'capitalize', bgcolor: 'green' }}>
                        Filter
                    </Button>
                </Grid>

                <Grid item lg={1} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Button onClick={handlePrint} fullWidth variant="contained" sx={{ textTransform: 'capitalize', bgcolor: 'green' }}>
                        Print
                    </Button>
                </Grid>

                <Grid item lg={1.5} sm={3.5} md={2.5} xs={12}>
                    <Button onClick={handleExport} fullWidth variant="contained" sx={{ textTransform: 'capitalize' }}>
                        Export to Excel
                    </Button>
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell>Total Qty Order</TableCell>
                            <TableCell>Total Qty Dispatch</TableCell>
                            <TableCell>Total Qty Remaining</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading && (
                            <TableCell colSpan={10}>
                                <Loader />
                            </TableCell>
                        )}
                        {!!orderItems &&
                            orderItems?.map((row, index) => (
                                <React.Fragment key={index}>
                                    <TableRow
                                        key={row.productName}
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            '&:hover': { bgcolor: '#f0f0f0', cursor: 'pointer' }
                                        }}
                                        onClick={() => handleRowClick(index)}
                                    >
                                        <TableCell component="th" scope="row">
                                            {row?.productName}
                                        </TableCell>
                                        <TableCell>{row?.totalQtyOrder}</TableCell>
                                        <TableCell>{row?.totalQtyDispatch}</TableCell>
                                        <TableCell>{row?.totalQtyRemaining}</TableCell>
                                    </TableRow>
                                    {expandedRow === index && (
                                        <TableRow style={{ backgroundColor: '#d8deff' }}>
                                            <TableCell colSpan={10}>
                                                <TableContainer>
                                                    <Table size="small" aria-label="a dense table">
                                                        <TableHead style={{ backgroundColor: '#d8deff' }}>
                                                            <TableRow>
                                                                <TableCell>Rep</TableCell>
                                                                <TableCell>Customer</TableCell>
                                                                <TableCell>Qty Order</TableCell>
                                                                <TableCell>Qty Dispatch</TableCell>
                                                                <TableCell>Status</TableCell>
                                                                <TableCell>Date Created</TableCell>
                                                                {/* Add more child column headers if needed */}
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody style={{ backgroundColor: '#fff9d8' }}>
                                                            {row?.list?.map((child, childIndex) => (
                                                                <TableRow key={childIndex}>
                                                                    <TableCell>
                                                                        {child?.user?.firstName} {child?.user?.lastName}
                                                                    </TableCell>
                                                                    <TableCell>{child?.customer?.name}</TableCell>
                                                                    <TableCell>{child?.qtyOrder}</TableCell>
                                                                    <TableCell>{child?.qtyDispatch}</TableCell>
                                                                    <TableCell>{child?.status}</TableCell>
                                                                    <TableCell>
                                                                        {moment(child?.createdOn).format('MMM DD, YYYY - hh:mm A')}
                                                                    </TableCell>
                                                                    {/* Add more child column data if needed */}
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        {!!error && (
                            <TableCell colSpan={10}>
                                <ErrorScreen error={error} />
                            </TableCell>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <div style={{ display: 'none' }}>
                <OrderItemPrint
                    ref={componentRef}
                    data={orderItems || []}
                    rep={selectedRep}
                    product={selectedProduct}
                    customer={selectedCustomer}
                    startDate={startDate}
                    endDate={endDate}
                />
            </div>
        </Layout>
    );
}
